import React from 'react';

import {
  cancelImportJob,
  setImportJobAcknowledge,
} from '~/components/Admin/UsersPage/UsersPage.data';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { BulkImportUserResultType } from '~/enums/BulkImportUserResultType';
import { BulkImportUserStatusType } from '~/enums/BulkImportUserStatusType';
import { CodedErrorCodeType } from '~/enums/CodedErrorCodeType';
import { useIntl } from 'react-intl';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { parseApiError } from '~/utils/parseApiError';

import DownloadImportResultsFileLink from './DownloadImportResultsFileLink';

export const useImportJobStatusBar = () => {
  const intl = useIntl();

  const handleApiError = (errorResponse) => {
    const error = parseApiError(errorResponse);
    showToast(ERROR, error.message);
  };

  const handleImportProcessCancelAction = async (clientId, importData) => {
    const { importId } = importData;
    try {
      await cancelImportJob({ clientId, importId });
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'ImportJobStatusBar.cancelSuccessMessage',
          defaultMessage: 'Canceled import job successfully.',
          description: 'Success message for canceling import job',
        }),
      );
    } catch (errorResponse) {
      handleApiError(errorResponse);
    }
  };

  const handleImportJobAcknowledgeClick = async (
    clientId,
    importData,
    handleAcknowledgeUpdate,
  ) => {
    const { importId } = importData;
    try {
      await setImportJobAcknowledge({ clientId, importId });

      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'ImportJobStatusBar.acknowledgeSuccessMessage',
          defaultMessage: 'Acknowledged import job successfully.',
          description: 'Success message for acknowledging import job',
        }),
      );
      handleAcknowledgeUpdate(importId);
    } catch (errorResponse) {
      handleApiError(
        errorResponse,
        CodedErrorCodeType.BulkImportErrorAcknowledgingJob,
      );
    }
  };

  const getErrorMessage = (prefixMessage, errors) =>
    intl.formatMessage(
      {
        id: 'ImportJobStatusBar.errorMessage',
        defaultMessage: '{prefixMessage} {errors} users data failed to import.',
        description:
          'Error message for failed imports. Example: prefixMessage = "Canceled -", errors = 5',
      },
      {
        prefixMessage,
        errors,
      },
    );

  const getErrorDownloadMessage = (
    importId,
    clientId,
    prefixMessage,
    errors,
  ) => {
    return (
      <>
        {getErrorMessage(prefixMessage, errors)}
        <DownloadImportResultsFileLink
          text={intl.formatMessage({
            id: 'ImportJobStatusBar.downloadErrorList',
            defaultMessage: 'Download the error list to fix errors.',
            description: 'Link text to download error list',
          })}
          importId={importId}
          clientId={clientId}
          resultType={BulkImportUserResultType.ProcessErrors}
        />
      </>
    );
  };

  const getFinishedStatusMessage = ({ importId, counters }, clientId) => {
    const { processed, total, errors } = counters;
    const finishedImportMessage = intl.formatMessage(
      {
        id: 'ImportJobStatusBar.processedMessage',
        defaultMessage:
          'Processed {processed} {processed, plural, one {user} other {users}} of {total}.',
        description:
          'Message showing processed count with pluralization. Example: processed = 18, total = 20',
      },
      {
        processed,
        total,
      },
    );

    if (errors === 0) return finishedImportMessage;

    return getErrorDownloadMessage(
      importId,
      clientId,
      finishedImportMessage,
      errors,
    );
  };

  const getCancelledStatusMessage = ({ counters }) => {
    const { processed, total, errors } = counters;
    const cancelledImportMessage = intl.formatMessage(
      {
        id: 'ImportJobStatusBar.cancelledMessage',
        defaultMessage:
          'Canceled - Processed {processed} {processed, plural, one {user} other {users}} of {total}.',
        description:
          'Message for canceled import with pluralization. Example: processed = 15, total = 20',
      },
      {
        processed,
        total,
      },
    );

    if (errors === 0) return cancelledImportMessage;

    return getErrorMessage(cancelledImportMessage, errors);
  };

  const barItemBaseProps = {
    [BulkImportUserStatusType.New]: {
      getIcon: () => 'loading',
      getBarItemText: () => (
        <>
          {intl.formatMessage({
            id: 'ImportJobStatusBar.usersImportInQueue',
            defaultMessage: 'Users import in queue.',
            description: 'Status message for queued import',
          })}
        </>
      ),
      getActions: ({ clientId, importData }) => [
        {
          onClick: () => handleImportProcessCancelAction(clientId, importData),
          text: intl.formatMessage({
            id: 'ImportJobStatusBar.cancelImportQueue',
            defaultMessage: 'Cancel Import',
            description: 'Button text to cancel import from queue',
          }),
        },
      ],
    },
    [BulkImportUserStatusType.Failed]: {
      getIcon: () => 'danger',
      getBarItemText: () => (
        <>
          {intl.formatMessage({
            id: 'ImportJobStatusBar.usersImportFailed',
            defaultMessage: 'Users import failed.',
            description: 'Status message for failed import',
          })}
        </>
      ),
      getActions: () => [],
      getDismissAction: ({ clientId, importData, handleAcknowledgeUpdate }) =>
        handleImportJobAcknowledgeClick(
          clientId,
          importData,
          handleAcknowledgeUpdate,
        ),
    },
    [BulkImportUserStatusType.Running]: {
      getIcon: () => 'loading',
      getBarItemText: ({ counters }) => (
        <>
          {intl.formatMessage(
            {
              id: 'ImportJobStatusBar.processedRunning',
              defaultMessage:
                'Processed {processed} {processed, plural, one {user} other {users}} of {total}.',
              description:
                'Status message for running import with pluralization. Example: processed = 10, total = 20',
            },
            {
              processed: counters.processed,
              total: counters.total,
            },
          )}
        </>
      ),
      getActions: ({ clientId, importData }) => [
        {
          onClick: () => handleImportProcessCancelAction(clientId, importData),
          text: intl.formatMessage({
            id: 'ImportJobStatusBar.cancelImport',
            defaultMessage: 'Cancel Import',
            description: 'Button text to cancel import',
          }),
        },
      ],
    },
    [BulkImportUserStatusType.Finished]: {
      getIcon: ({ errors }) => (errors === 0 ? 'success' : 'warning'),
      getBarItemText: (importData, clientId) =>
        getFinishedStatusMessage(importData, clientId),
      getActions: () => [],
      getDismissAction: ({ clientId, importData, handleAcknowledgeUpdate }) =>
        handleImportJobAcknowledgeClick(
          clientId,
          importData,
          handleAcknowledgeUpdate,
        ),
    },
    [BulkImportUserStatusType.Cancelled]: {
      getIcon: () => 'warning',
      getBarItemText: (importData, clientId) =>
        getCancelledStatusMessage(importData, clientId),
      getActions: () => [],
      getDismissAction: ({ clientId, importData, handleAcknowledgeUpdate }) =>
        handleImportJobAcknowledgeClick(
          clientId,
          importData,
          handleAcknowledgeUpdate,
        ),
    },
  };

  return barItemBaseProps;
};
